/* eslint-disable react-hooks/refs */
'use client';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { Button } from '@/ui-components/atoms/Button/Button';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { FlexibleFormBase, FlexibleFormVariant } from './FlexibleFormBase';
import { parseFormSubmitResponse } from './flexibleFormShared';
import styles from './widgetform.module.scss';

const {
    flexForm,
    halfWidth,
    fullWidth,
    formSectionHeaderStyles,
    formSectionTextBlockStyles,
    dropdownSelectStyles,
    successHeadlineStyles,
    successErrorWrapper,
    wrapperRow,
    stateContainer,
    stateWrapper,
    zipInputStyles,
    selectorStyles,
    subheadlineStyles,
    ctaStyles,
    duplicationFormError,
    widgetContainer,
    widgetForm,
    submitButtonWrapper,
    submitted,
    ctaCaption,
} = styles;

interface ClientFlexibleFormProps {
    asset: SiteFlexibleForm;
}

export function createWidgetFormLayoutConfig(
    asset: SiteFlexibleForm,
): Pick<FlexibleFormVariant, 'styles' | 'renderContent'> {
    return {
        styles: {
            flexForm,
            halfWidth,
            fullWidth,
            formSectionHeaderStyles,
            formSectionTextBlockStyles,
            dropdownSelectStyles,
            wrapperRow,
            stateContainer,
            stateWrapper,
            zipInputStyles,
            selectorStyles,
        },
        renderContent: ({ wrappedFieldElements, duplicateError, duplicateErrorMessage, submitButton }) => (
            <Grid container className={widgetContainer}>
                <Grid col={{ sm: 12, md: 8, lg: 12 }} start={{ md: 3 }} className={widgetForm}>
                    <Grid col={{ sm: 12, lg: 12 }}>
                        <div className={flexForm}>{wrappedFieldElements}</div>
                    </Grid>
                    <div className={submitButtonWrapper}>
                        {duplicateError && (
                            <StyledContainer>
                                <Text tag="div" className={duplicationFormError} role="alert">
                                    {duplicateErrorMessage}
                                </Text>
                            </StyledContainer>
                        )}
                        {submitButton}
                        {asset.fields.cta_caption && (
                            <RenderRTE contentfulDoc={asset.fields.cta_caption} className={ctaCaption} />
                        )}
                    </div>
                </Grid>
            </Grid>
        ),
    };
}

export function createWidgetFormVariantOverrides(asset: SiteFlexibleForm): Partial<FlexibleFormVariant> {
    const {
        fields: { type, success_headline, error_headline, success_subheadline, error_subheadline, error_cta },
    } = asset;
    const formType = type.toLowerCase();

    return {
        analytics: {
            successEvent: `${formType}-success`,
            failEvent: `${formType}-fail`,
        },
        sourceName: undefined,
        trackSubmitAttempted: false,
        extraDropdownTypes: ['nfl favorite team'],
        getStateSelectHeader: () => ({
            title: 'Select a state',
            body: null,
        }),
        getDropdownSelectHeader: () => ({
            title: 'Select',
            body: null,
        }),
        onSuccessResponse: async (response, { validations, setValidations, setSubmitState }) => {
            const json = await response.json();
            const parsedResponse = parseFormSubmitResponse(json, validations);

            if (parsedResponse.submitState) {
                setSubmitState(parsedResponse.submitState);
            }

            if (Object.keys(parsedResponse.serverValidations).length > 0) {
                setValidations((current) => ({ ...current, ...parsedResponse.serverValidations }));
            }
        },
        renderSubmitMessage: ({ submitState }) => {
            const success = submitState === 'success';
            const failed = submitState === 'failed';
            const errorCTA = error_cta?.fields;

            return (
                <div className={successErrorWrapper}>
                    {success && (
                        <ImageWrapper
                            src="https://www.siriusxm.com/content/dam/sxm-com/lottie/stella/Stella-Wag-Animation.gif"
                            alt="Stella Alert"
                            optimizedWidth={100}
                            optimizedHeight={100}
                            loadingStrategy="eager"
                            skipOptimization={true}
                        />
                    )}
                    <div data-cy={`${formType}-form-submit-success-message`} className={successHeadlineStyles}>
                        <Text tag="h5" variant="h5">
                            {success && success_headline}
                            {failed && error_headline}
                        </Text>
                    </div>
                    <Text tag="p" className={subheadlineStyles}>
                        {success && <RenderRTE contentfulDoc={success_subheadline} />}
                        {failed && <RenderRTE contentfulDoc={error_subheadline} />}
                    </Text>
                    {failed && errorCTA && (
                        <>
                            <Button
                                type="button"
                                className={ctaStyles}
                                href={errorCTA.href}
                                linkTarget={errorCTA.link_target}
                                ariaLabel={errorCTA.name}
                                variant={errorCTA.theme}
                                size={errorCTA.size.toLowerCase()}
                                fitting="hug-contents"
                                dataCy="sweeps-form-error-cta"
                            >
                                {errorCTA.link_text}
                            </Button>
                            {asset.fields.cta_caption && (
                                <RenderRTE contentfulDoc={asset.fields.cta_caption} className={ctaCaption} />
                            )}
                        </>
                    )}
                </div>
            );
        },
        renderShell: ({ submitState, submitMessage, form }) => (
            <>
                {submitState !== 'untouched' && submitState !== 'duplicate' ? (
                    <div aria-live="polite" className={submitted}>
                        {submitMessage}
                    </div>
                ) : undefined}
                {submitState === 'untouched' || submitState === 'duplicate' ? form : undefined}
            </>
        ),
    };
}

export function WidgetForm({ asset }: ClientFlexibleFormProps) {
    const formType = asset.fields.type.toLowerCase();
    const widgetFormVariant = {
        formType,
        ...createWidgetFormLayoutConfig(asset),
        ...createWidgetFormVariantOverrides(asset),
    } as FlexibleFormVariant;

    return <FlexibleFormBase asset={asset} variant={widgetFormVariant} />;
}
